import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ShieldX, ExternalLink, ArrowLeft } from 'lucide-react';

// A staff UDST ID arriving through a lab's D2L link ("Test as a student").
const STAFF_TEST_MESSAGES = {
  STAFF_TEST_LINK_CLOSED: `You opened this lab's link as staff. To test it as a student, open the lab in Virtual Labs, choose "Test the D2L link", then open this link again within 30 minutes.`,
  STAFF_TEST_NEEDS_LAB: `You signed in from D2L as staff. To try a lab as a student, open that lab's own link.`,
  STAFF_TEST_REFUSED: 'This lab cannot be tested as a student right now: it is closed or closing, or you do not manage it.',
};

/**
 * Access Denied Page
 * Shown when a student tries to access a D2L-required lab without D2L referrer,
 * or when staff open a lab's D2L link without "Test the D2L link" first.
 */
export default function AccessDenied() {
  const [searchParams] = useSearchParams();

  const reason = searchParams.get('reason');
  const labId = searchParams.get('labId');
  const studentId = searchParams.get('studentId');
  const staffTest = reason === 'staff_test';
  const staffTestMessage = STAFF_TEST_MESSAGES[searchParams.get('code')]
    || 'Your test environment could not be opened. Try again from the lab page in Virtual Labs.';

  const d2lUrl = process.env.REACT_APP_D2L_URL || 'https://d2l.udst.edu.qa';

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, var(--login-bg-start) 0%, var(--login-bg-mid) 50%, var(--login-bg-start) 100%)',
      padding: 20,
    }}>
      <div
        style={{
          width: '100%',
          maxWidth: 480,
          background: 'var(--ad-card-bg)',
          borderRadius: 16,
          padding: 40,
          boxShadow: 'var(--login-card-shadow)',
          border: '1px solid var(--login-card-border)',
          textAlign: 'center',
        }}
        role="alert"
        aria-labelledby="access-denied-title"
      >
        {/* Icon */}
        <div style={{
          width: 72,
          height: 72,
          borderRadius: 18,
          background: 'var(--ad-danger-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          border: '1px solid var(--ad-danger-border)',
        }} aria-hidden="true">
          <ShieldX size={36} style={{ color: 'var(--accent-danger)' }} />
        </div>

        {/* Title */}
        <h1 id="access-denied-title" style={{
          fontSize: 24,
          fontWeight: 700,
          color: 'var(--login-heading-color)',
          marginBottom: 8,
        }}>
          {staffTest ? 'Testing as a student' : 'Access Denied'}
        </h1>

        {/* Message */}
        {staffTest ? (
          <>
            <p style={{
              fontSize: 16,
              color: 'var(--login-subtext-color)',
              lineHeight: 1.6,
              marginBottom: 24,
            }}>
              {staffTestMessage}
            </p>
            {/^[a-f0-9]{24}$/.test(labId || '') && (
              <a href={`/labs/${labId}`} className="btn btn-primary" style={{ marginBottom: 24, display: 'inline-flex', gap: 8 }}>
                <ExternalLink size={18} />
                Open the lab in Virtual Labs
              </a>
            )}
          </>
        ) : reason === 'd2l_required' ? (
          <>
            <p style={{
              fontSize: 16,
              color: 'var(--login-subtext-color)',
              lineHeight: 1.6,
              marginBottom: 24,
            }}>
              This lab requires you to access it through <strong style={{ color: 'var(--login-heading-color)' }}>D2L (Brightspace)</strong>.
              Please go to D2L and click on the lab link from your course.
            </p>

            <div style={{
              background: 'var(--ad-info-bg)',
              border: '1px solid var(--ad-info-border)',
              borderRadius: 12,
              padding: 20,
              marginBottom: 24,
            }}>
              <p style={{
                fontSize: 14,
                color: 'var(--ad-info-color)',
                marginBottom: 16,
              }}>
                <strong>Why?</strong> Your instructor has enabled D2L-only access for this lab to ensure academic integrity.
              </p>

              <a
                href={d2lUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, var(--sotp-accent-start), var(--sotp-accent-end))',
                  color: 'white',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px -10px rgba(59, 130, 246, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <ExternalLink size={18} />
                Go to D2L
              </a>
            </div>
          </>
        ) : (
          <p style={{
            fontSize: 16,
            color: 'var(--login-subtext-color)',
            lineHeight: 1.6,
            marginBottom: 24,
          }}>
            You don't have permission to access this resource.
            Please contact your instructor if you believe this is an error.
          </p>
        )}

        {/* Student ID Info */}
        {studentId && (
          <p style={{
            fontSize: 13,
            color: 'var(--login-muted-color)',
            marginBottom: 16,
          }}>
            Student ID: {studentId}
          </p>
        )}

        {/* Back Link */}
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--text-secondary)',
            fontSize: 14,
            textDecoration: 'none',
          }}
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>
      </div>
    </div>
  );
}
